import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationResolution, ApplicationStatus } from "model/Application";
import type { ApplicationDto, ApplicationRequestDto } from "model/Application";
import { GigType } from "model/Gig";
import { ColorV2 } from "config/colors";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { CustomIcon } from "components/icon/customIcon.component";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { TextArea } from "components/designV2/textarea/textarea";
import { Tag } from "components/designV2/tag/tag";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useApplicationStore } from "web-apps/company/stores/applicationStore/applicationStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { formatWorkerDateOfBirth } from "web-apps/company/utils/utils";
import { ApplicantDetailsModal } from "web-apps/company/components/applicantDetails/applicantDetailsModal.component";
import {
  ApplicationActions,
  ApplicantIdentity,
  ApplicantRequestedRate,
  ApplicantRow,
  ApplicationSelection,
  ApplicationStatusChipWrapper,
  ApplicantExpandIcon,
  ApplicationModalActions,
  ApplicationModalContent,
} from "./applicationList.styled";
import { toApplicationStatusChip } from "./applicationList.display.logic";
import { toApplicationStatusUpdate } from "./applicationStatus.logic";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";

const ASSIGN_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

const DESTRUCTIVE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.DeclinedRed,
  activeBackgroundColor: ColorV2.NegativeRed,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

const REJECT_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  borderColor: ColorV2.NegativeRed,
  textColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.NegativeRedLight,
  activeBackgroundColor: ColorV2.WithdrawnPink,
};

type Props = {
  readonly application: ApplicationDto;
  readonly handleShowContract: (application: ApplicationDto) => void;
  readonly onApplicationUpdated?: (application: ApplicationDto) => void;
  readonly selectedApplications: readonly ApplicationDto[];
  readonly setSelectedApplications: React.Dispatch<
    React.SetStateAction<ApplicationDto[]>
  >;
  readonly showContract: boolean;
  readonly showSelection: boolean;
};

export const ApplicationListItem: React.FC<Props> = ({
  application,
  handleShowContract,
  onApplicationUpdated,
  showContract,
  showSelection,
  selectedApplications,
  setSelectedApplications,
}) => {
  const { t } = useTranslation();
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [companyState] = useCompanyStore();
  const [applicationState, applicationActions, applicationDispatch] =
    useApplicationStore();
  const [, , alertDispatch] = useAlertStore();
  const [showApplicationDetails, setShowApplicationDetails] = useState(false);
  const selected = selectedApplications.some(
    (selectedApplication) => selectedApplication.id === application.id,
  );
  const workerName = `${application.worker.firstName} ${application.worker.lastName}`;
  const dateOfBirth = application.worker.dateOfBirth?.trim()
    ? formatWorkerDateOfBirth(application.worker.dateOfBirth)
    : "";
  const statusChip = toApplicationStatusChip(application);
  const isPublicShift = application.gig.type === GigType.PUBLIC;
  // Prefer the backend image id and keep the legacy identifier as a fallback.
  const workerImageId =
    application.worker.externalId?.trim() ||
    application.worker.firebaseId?.trim() ||
    null;
  const avatarUrl = useWorkerAvatar({
    workerExternalId: workerImageId,
    initialUrl:
      (workerImageId
        ? companyState.companyWorkerImages?.[workerImageId]
        : null) ?? null,
  });

  const handleSelectionChange = (isSelected: boolean): void => {
    setSelectedApplications((currentApplications) =>
      isSelected
        ? [...currentApplications, application]
        : currentApplications.filter(
            (currentApplication) => currentApplication.id !== application.id,
          ),
    );
  };

  const withdrawOffer = async (): Promise<void> => {
    const updatedApplication: ApplicationDto = {
      ...application,
      status: ApplicationStatus.CLOSED,
      resolution: ApplicationResolution.WITHDRAWN,
    };
    const req: ApplicationRequestDto = updatedApplication;

    if (!companyState.company || isWithdrawing) return;
    setIsWithdrawing(true);
    try {
      const response = await applicationActions.updateApplicationStatus(
        companyState.company.id,
        application.id,
        req,
      );
      const savedApplication = response?.data ?? updatedApplication;
      setWithdrawModalOpen(false);
      onApplicationUpdated?.(savedApplication);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("GigsCompany.Withdrawn"),
          message: t("GigsCompany.OfferWithdrawn"),
        },
      });
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "cross",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    } finally {
      setIsWithdrawing(false);
    }
  };

  const updateApplicationStatus = async (
    status: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
    applicant: ApplicationDto,
  ): Promise<void> => {
    if (!companyState.company) return;
    const statusUpdate = toApplicationStatusUpdate(status);
    const data: ApplicationDto = {
      ...applicant,
      ...statusUpdate,
      ...(statusUpdate.resolution === ApplicationResolution.REJECTED && {
        declineReason,
      }),
    };
    setIsUpdating(true);
    try {
      const response = await applicationActions.updateApplicationStatus(
        companyState.company.id,
        applicant.id,
        data,
      );
      const updatedApplication = response?.data ?? data;
      if (statusUpdate.resolution === ApplicationResolution.REJECTED) {
        setRejectModalOpen(false);
        setDeclineReason("");
      }
      onApplicationUpdated?.(updatedApplication);
      if (!onApplicationUpdated) {
        applicationDispatch({
          type: "GET_PENDING_APPLICATIONS_COUNT",
          payload: applicationState.applicationsCount - 1,
        });
      }
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t(
            statusUpdate.resolution === ApplicationResolution.ACCEPTED
              ? "ApplicationsCompany.Assigned"
              : "ApplicationsCompany.Rejected",
          ),
          message: t(
            statusUpdate.resolution === ApplicationResolution.ACCEPTED
              ? "ApplicationsCompany.ApplicantHasBeenAssignedGig"
              : "ApplicationsCompany.ApplicationHasBeenRejected",
          ),
        },
      });
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("ApplicationsCompany.ApplicationCouldNotBeUpdated"),
        },
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <ApplicantRow
        role="button"
        tabIndex={0}
        aria-label={workerName}
        onClick={() => setShowApplicationDetails(true)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setShowApplicationDetails(true);
        }}
      >
        <ApplicationSelection
          onClick={
            showSelection ? (event) => event.stopPropagation() : undefined
          }
          onKeyDown={
            showSelection ? (event) => event.stopPropagation() : undefined
          }
        >
          {showSelection && (
            <Checkbox
              checked={selected ? "checked" : "unchecked"}
              onChange={handleSelectionChange}
            />
          )}
        </ApplicationSelection>
        <ApplicantIdentity $hasSelection={showSelection}>
          <AvatarChip
            title={workerName}
            subTitle={dateOfBirth}
            avatarUrl={avatarUrl}
          />
        </ApplicantIdentity>
        <ApplicantRequestedRate>
          {isPublicShift && (
            <>
              <Text typography="b1" color={ColorV2.Blue1}>
                {application.requestedRate} {t("General.Currency")}
              </Text>
              <Text typography="s1" color={ColorV2.Blue4}>
                {t("ProfileWorker.RequestedRate")}
              </Text>
            </>
          )}
        </ApplicantRequestedRate>
        <ApplicationActions
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {showContract && (
            <SecondaryButton
              size="small"
              label={t("GigsWorker.Contract")}
              onClick={() => handleShowContract(application)}
            />
          )}
          {application.status === ApplicationStatus.PENDING && (
            <>
              <SecondaryButton
                size="small"
                label={t("GigsCompany.Reject")}
                styleOverrides={REJECT_BUTTON_STYLES}
                disabled={isUpdating}
                onClick={() => setRejectModalOpen(true)}
              />
              <PrimaryButton
                size="small"
                label={t("ApplicationsCompany.Assign")}
                styleOverrides={ASSIGN_BUTTON_STYLES}
                disabled={isUpdating}
                onClick={() =>
                  void updateApplicationStatus(
                    ApplicationStatus.OFFERED,
                    application,
                  )
                }
              />
            </>
          )}
          {application.status === ApplicationStatus.OFFERED && (
            <SecondaryButton
              size="small"
              label={t("GigsCompany.CancelOffer")}
              styleOverrides={REJECT_BUTTON_STYLES}
              onClick={() => setWithdrawModalOpen(true)}
            />
          )}
          {statusChip && (
            <ApplicationStatusChipWrapper>
              <Tag
                backgroundColor={statusChip.backgroundColor}
                text={t(statusChip.labelKey)}
                textColor={statusChip.textColor}
              />
            </ApplicationStatusChipWrapper>
          )}
        </ApplicationActions>
        <ApplicantExpandIcon>
          <CustomIcon
            name="v2/right-chevron"
            size="20px"
            color={ColorV2.Blue4}
          />
        </ApplicantExpandIcon>
      </ApplicantRow>
      <Modal
        isOpen={withdrawModalOpen}
        label={t("GigsCompany.WithdrawOffer")}
        onClose={() => setWithdrawModalOpen(false)}
        size="compact"
        footer={
          <ApplicationModalActions>
            <SecondaryButton
              label={t("General.Abort")}
              onClick={() => setWithdrawModalOpen(false)}
              disabled={isWithdrawing}
            />
            <PrimaryButton
              label={t("GigsCompany.CancelOffer")}
              isLoading={isWithdrawing}
              onClick={() => void withdrawOffer()}
            />
          </ApplicationModalActions>
        }
      >
        <ApplicationModalContent>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("GigsCompany.AreYouSureWithdrawOffer")}
          </Text>
        </ApplicationModalContent>
      </Modal>
      <Modal
        isOpen={rejectModalOpen}
        label={t("GigsCompany.YouAreAboutToRejectApplication", {
          user: application.worker.firstName,
        })}
        onClose={() => {
          setRejectModalOpen(false);
          setDeclineReason("");
        }}
        showCloseButton={false}
        size="compact"
        footer={
          <ApplicationModalActions>
            <SecondaryButton
              label={t("GigsCompany.Discard")}
              onClick={() => {
                setRejectModalOpen(false);
                setDeclineReason("");
              }}
              disabled={isUpdating}
            />
            <PrimaryButton
              label={t("GigsCompany.Reject")}
              styleOverrides={DESTRUCTIVE_BUTTON_STYLES}
              isLoading={isUpdating}
              disabled={isUpdating}
              onClick={() =>
                void updateApplicationStatus(
                  ApplicationStatus.CLOSED,
                  application,
                )
              }
            />
          </ApplicationModalActions>
        }
      >
        <ApplicationModalContent>
          <TextArea
            label={t("GigsCompany.NoteToApplicant")}
            aria-label={t("GigsCompany.NoteToApplicant")}
            placeholder={t("GigsCompany.SendANote")}
            value={declineReason}
            onChange={setDeclineReason}
            disabled={isUpdating}
          />
        </ApplicationModalContent>
      </Modal>
      {showApplicationDetails && (
        <ApplicantDetailsModal
          applicant={application}
          onApplicationUpdated={onApplicationUpdated}
          setShowApplicationModal={setShowApplicationDetails}
          showApplicationModal={showApplicationDetails}
          presentation="panel"
        />
      )}
    </>
  );
};
